import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { ProgramCard } from "@/components/programs/ProgramCard";
import { Highlight } from "@/components/ui";
import { useAllPrograms } from "@/lib/programs";
import { Seo } from "@/lib/seo/Seo";

function FilterGroup({ label, options, value, onChange, testId }) {
  if (options.length < 2) return null;
  return (
    <div data-testid={testId}>
      <p className="section-eyebrow mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              data-testid={`${testId}-${option}`}
              onClick={() => onChange(active ? "" : option)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
                active
                  ? "bg-brand-500 text-white border-brand-500"
                  : "bg-white text-ink/70 border-brand-200 hover:border-brand-500"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * All programmes: virtual internships and skill development programmes (always listed),
 * plus in-person programmes from the backend.
 */
export default function Programs() {
  const { programs: all } = useAllPrograms();
  const [params, setParams] = useSearchParams();

  const kindOptions = useMemo(() => [...new Set(all.map((p) => p.kind).filter(Boolean))], [all]);
  const modeOptions = useMemo(() => [...new Set(all.map((p) => p.mode).filter(Boolean))].sort(), [all]);

  const f = {
    kind: params.get("type") || "",
    mode: params.get("mode") || "",
  };

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const reset = () => setParams({}, { replace: true });

  const filtered = useMemo(
    () => all.filter((p) => (!f.kind || p.kind === f.kind) && (!f.mode || p.mode === f.mode)),
    [all, f.kind, f.mode],
  );

  const isTypedProgramPage =
    f.kind === "Virtual internship" ||
    f.kind === "Skill development" ||
    f.kind === "In-person programme";
  const showIntroBlurb = !isTypedProgramPage;
  const eyebrow = isTypedProgramPage ? "Discover Your Ideal Programme" : "Find a programme";

  return (
    <SiteLayout>
      <Seo
        title="All Programmes for Students | A Learning Lab India"
        description="Browse virtual internships, skill development programmes, Delhi bootcamps and observership programmes for students in India. Filter by type and delivery mode."
        path="/programs"
      />
      <div data-testid="programs-page" className="page-main page-container">
        <p className="section-eyebrow mb-4">{eyebrow}</p>
        <h1 className={`page-title ${showIntroBlurb ? "mb-3" : "mb-10"}`}>
          Browse <Highlight>all programmes</Highlight>.
        </h1>
        {showIntroBlurb ? (
          <p className="text-ink/70 mb-10 max-w-2xl">
            Virtual internships, 4-week skill development programmes, Delhi bootcamps and observership programmes for students in India.
          </p>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl border border-brand-200 p-6 space-y-6 shadow-sm" data-testid="filter-toolbar">
              <FilterGroup label="Type" options={kindOptions} value={f.kind} onChange={(v) => set("type", v)} testId="filter-type" />
              <FilterGroup label="Mode" options={modeOptions} value={f.mode} onChange={(v) => set("mode", v)} testId="filter-mode" />

              <div className="pt-4 border-t border-brand-200 space-y-3">
                <p className="text-sm text-ink/70" data-testid="result-count">
                  Showing {filtered.length} programmes
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="filter-reset"
                  className="text-xs font-semibold uppercase tracking-wide text-brand-600 flex items-center gap-1 hover:underline"
                >
                  <X size={14} /> Reset filters
                </button>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-ink/70 bg-white rounded-2xl border border-brand-200">
                No programmes match those filters.{" "}
                <button type="button" onClick={reset} className="underline text-brand-600">
                  Reset
                </button>
              </div>
            ) : (
              <ul className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="programs-grid">
                {filtered.map((p) => (
                  <li key={p.id}>
                    <ProgramCard program={p} testId={`programs-card-${p.slug}`} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
